import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * FloatingInput — text input with a label that animates from inside the field
 * up into the top border on focus or when the field has a value.
 *
 * Drop-in for the existing `Input` primitive. Forwards refs so it works with
 * react-hook-form's `register` and `Controller`. Honors the 16px minimum
 * input font on mobile (no iOS auto-zoom).
 */
export interface FloatingInputProps extends React.ComponentProps<'input'> {
  label: string;
  /** Optional adornment rendered inside the field on the left (e.g. "$"). */
  leftAdornment?: React.ReactNode;
  /** Visual error state — adds destructive border/focus ring. */
  hasError?: boolean;
  containerClassName?: string;
  /** When false, the label is used as a normal placeholder and the floating label is hidden. */
  floating?: boolean;
}

export const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ label, leftAdornment, hasError, className, containerClassName, id, value, defaultValue, placeholder, floating = true, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? `fi-${generatedId}`;
    const isFloating = floating;
    return (
      <div className={cn('relative', containerClassName)}>
        {leftAdornment && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground/60 z-10 flex items-center justify-center w-4">
            {leftAdornment}
          </span>
        )}
        <input
          id={inputId}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          // Use a single space placeholder so :placeholder-shown works as our "empty" signal when floating.
          placeholder={isFloating ? (placeholder ?? ' ') : placeholder}
          aria-label={!isFloating ? label : undefined}
          className={cn(
            'peer flex h-14 w-full rounded-lg field-surface field-border px-3 text-base appearance-none transition-colors',
            isFloating ? 'pt-5 pb-1.5 placeholder:text-transparent' : 'py-3 placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:border-white/30',
            'disabled:cursor-not-allowed disabled:opacity-50',
            leftAdornment && 'pl-9',
            hasError && 'border-destructive/60 focus-visible:border-destructive',
            className
          )}
          {...props}
        />
        <label
          htmlFor={inputId}
          className={cn(
            isFloating
              ? 'pointer-events-none absolute left-3 top-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 transition-all'
              : 'sr-only',
            isFloating && [
              // When empty AND not focused, drop label to vertical center as the placeholder.
              'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-muted-foreground/60',
              // Snap back up on focus even when empty.
              'peer-focus:top-1.5 peer-focus:-translate-y-0 peer-focus:text-[11px] peer-focus:font-medium peer-focus:uppercase peer-focus:tracking-wide peer-focus:text-muted-foreground/80',
              leftAdornment && 'left-9 peer-placeholder-shown:left-9 peer-focus:left-3',
              hasError && 'text-destructive peer-focus:text-destructive'
            ]
          )}
        >
          {label}
        </label>
      </div>
    );
  }
);
FloatingInput.displayName = 'FloatingInput';

export interface FloatingTextareaProps extends React.ComponentProps<'textarea'> {
  label: string;
  hasError?: boolean;
  containerClassName?: string;
}

export const FloatingTextarea = React.forwardRef<HTMLTextAreaElement, FloatingTextareaProps>(
  ({ label, hasError, className, containerClassName, id, placeholder, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? `ft-${generatedId}`;
    return (
      <div className={cn('relative', containerClassName)}>
        <textarea
          id={inputId}
          ref={ref}
          placeholder={placeholder ?? ' '}
          className={cn(
            'peer flex min-h-[96px] w-full rounded-lg field-surface field-border px-3 pt-6 pb-2 text-base resize-y',
            'placeholder:text-transparent transition-colors',
            'focus-visible:outline-none focus-visible:border-white/30',
            'disabled:cursor-not-allowed disabled:opacity-50',
            
            hasError && 'border-destructive/60 focus-visible:border-destructive',
            className
          )}
          {...props}
        />
        <label
          htmlFor={inputId}
          className={cn(
            'pointer-events-none absolute left-3 top-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 transition-all',
            'peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-muted-foreground/60',
            'peer-focus:top-1.5 peer-focus:text-[11px] peer-focus:font-medium peer-focus:uppercase peer-focus:tracking-wide peer-focus:text-muted-foreground/80',
            hasError && 'text-destructive peer-focus:text-destructive'
          )}
        >
          {label}
        </label>
      </div>
    );
  }
);
FloatingTextarea.displayName = 'FloatingTextarea';

/**
 * FloatingSelectField — wraps any select-style trigger (e.g. shadcn SelectTrigger,
 * combobox button) so it picks up the same floating-label styling as FloatingInput.
 *
 * Usage:
 *   <FloatingSelectField label="Game" hasValue={!!field.value}>
 *     <Select onValueChange={field.onChange} value={field.value}>
 *       <SelectTrigger className="floating-trigger" />
 *       ...
 *     </Select>
 *   </FloatingSelectField>
 *
 * The inner trigger should pass `className="floating-trigger"` so we can target it
 * with the right padding. Falls back to a wrapping div otherwise.
 */
export function FloatingSelectField({
  label,
  hasValue,
  hasError,
  className,
  leftAdornment,
  children,
}: {
  label: string;
  hasValue: boolean;
  hasError?: boolean;
  className?: string;
  leftAdornment?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn('relative floating-select-wrap', className)}
      data-has-value={hasValue ? 'true' : 'false'}
    >
      {leftAdornment && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 z-10">
          {leftAdornment}
        </span>
      )}
      <span
        className={cn(
          'pointer-events-none absolute z-10 transition-all',
          leftAdornment ? 'left-9' : 'left-3',
          hasValue
            ? 'top-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80'
            : 'top-1/2 -translate-y-1/2 text-base text-muted-foreground/60',
          hasError && 'text-destructive'
        )}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * SectionHeader — minimal eyebrow used in the Edit Item modal to replace
 * heavy nested cards with a single hairline divider + small title.
 */
export function SectionHeader({ title, hint, className }: { title: string; hint?: string; className?: string }) {
  return (
    <div className={cn('flex items-baseline justify-between pt-2', className)}>
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">{title}</h4>
      {hint && <span className="text-[11px] text-muted-foreground/60">{hint}</span>}
    </div>
  );
}

/**
 * FieldGroup — consistent two-up grid wrapper for paired fields.
 * Stacks on very narrow screens, two-up on >=380px.
 */
export function FieldGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-3', className)}>{children}</div>;
}

/**
 * FormSection — titled card wrapper that groups related fields visually.
 * Use for both the Add wizard and the flat Edit form so the modal scans
 * as a few short sections rather than one long flat list.
 */
export function FormSection({
  title,
  description,
  children,
  className,
  bare,
}: {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  /** Skip the bordered card chrome (use for full-bleed sections like the mode switcher). */
  bare?: boolean;
}) {
  if (bare) {
    return <div className={cn('space-y-3', className)}>{children}</div>;
  }
  return (
    <section
      className={cn(
        'rounded-xl border border-white/[0.06] bg-black/20 p-4 space-y-4',
        className
      )}
    >
      {(title || description) && (
        <header className="space-y-0.5">
          {title && <h3 className="text-sm font-semibold text-foreground">{title}</h3>}
          {description && <p className="text-xs text-muted-foreground/80">{description}</p>}
        </header>
      )}
      {children}
    </section>
  );
}
